import { Injectable } from '@angular/core';
import { AccountSignals } from '../../../pages/admin/territories/signals';
import {
  AiChatChartSpec,
  AiChatTurn,
} from 'src/app/shared/components/ai-chat-panel/ai-chat-panel.component';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, defer, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { RAW_HTTP_ERROR } from '../../helpers/error.interceptor';

export interface Rep {
  id: number;
  name: string;
  email: string;
  /** Set on a territory/region's own `reps` array; the primary rep sorts first there. Not
   *  meaningful on the flat rep pool (`repPool`/`reps`). */
  is_primary?: boolean;
}
export interface TerritoryMetrics {
  zips: number;
  facilities: number;
  active_accounts: number;
  t12m_revenue: number;
  /** Paid units in the period, then the free-of-charge ($0) orders, units and FOC-only accounts. */
  units: number;
  foc_orders: number;
  foc_units: number;
  foc_only: number;
  overrides: number;
  reps: number;
}
export interface Territory {
  id: number;
  region_id: number;
  name: string;
  code: string;
  /** null while live; retiring unassigns its ZIPs and frees the name for reuse. */
  retired_on: string | null;
  is_open: boolean;
  reps: Rep[];
  /** Rep tree only: the rep owns it — directly, through a region or by override. */
  own?: boolean;
  /** Empty on a retired territory — the backend stops computing metrics once it's retired. */
  metrics: Partial<TerritoryMetrics>;
}
export interface Region {
  id: number;
  parent_id: number | null;
  name: string;
  code: string;
  /** null while live; retiring requires no live territories or sub-regions under it. */
  retired_on: string | null;
  reps: Rep[];
}
export interface Tree {
  regions: Region[];
  territories: Territory[];
  totals: {
    unassigned_facilities: number;
    no_address_facilities: number;
    total_facilities: number;
    lapsed_facilities: number;
    /** Shrinking / Expanding: last 90 days vs the 90 before (see AccountSignals). */
    declining_facilities?: number;
    expanding_facilities?: number;
    /** FOC orders in the last 24 months, no paid order in them. */
    foc_only_facilities: number;
    units: number;
    foc_units: number;
    uncovered_ordering_facilities: number;
    uncovered_ordering_zips: number;
    /** Pinned with no reps: CS-only accounts hidden from every field rep. */
    house_facilities: number;
    /** ZIPs that aren't a resolvable US ZIP — the `zip_unknown` facility filter. */
    zip_unknown_facilities: number;
  };
}
export interface EffectiveRow {
  user_id: number | null;
  source: 'territory' | 'region' | 'open' | 'override';
  territory_id: number | null;
}
/** Overrides and pins are set on one facility or on every location of its parent account
 *  (`facility.company_id`), current and future. Facility rows win over parent-account rows. */
export type OverrideGrain = 'facility' | 'parent_account';
/** Which grain a facility action writes to. Defaults to the facility on the backend. */
export type ApplyTo = OverrideGrain;
export interface Override {
  id: number;
  user_id: number;
  facility_id: number;
  is_exclusion: boolean;
  note: string | null;
  created_by: number;
  created_date: string;
  grain: OverrideGrain;
}
export type FacilityFlag =
  | 'zip_in_no_territory'
  | 'no_delivery_address'
  | 'unassigned'
  | 'override_stale'
  | 'override_redundant'
  | 'override_orphan'
  | 'lapsed'
  /** Got free-of-charge orders in the last 24 months but no paid one. */
  | 'foc_only'
  /** Pinned with no reps — no field rep, CS only. */
  | 'house'
  /** Ignores the ZIP rules: keeps its override reps whatever happens to the territory. */
  | 'pinned'
  /** At least one override or pin comes from the parent account, not this facility. */
  | 'account_override'
  /** The ZIP isn't a resolvable US ZIP. */
  | 'zip_unknown'
  /** The ZIP is malformed but still resolves. */
  | 'zip_malformed';
/** The 90-day signals: `up` = expanding, `down` = shrinking, `flat` = paid orders in the period but
 *  neither, `none` = no paid orders in the period. */
export type FacilityTrend = 'up' | 'flat' | 'down' | 'none';
export interface FacilityRow {
  id: number;
  name: string;
  zip5: string | null;
  city: string;
  state: string;
  /** Accepted by the seller (`facility.vetted`); the map's status toggle filters on it. */
  vetted: boolean;
  /** Geocoded delivery address (WGS84); null until the address has been geocoded, in which
   *  case the map falls back to the ZIP centroid. */
  lat: number | null;
  lng: number | null;
  territory_id: number | null;
  effective: EffectiveRow[];
  overrides: Override[];
  flags: FacilityFlag[];
  orders_12m: number;
  revenue_12m: number;
  /** Paid units in the period. */
  units_12m: number;
  /** Free-of-charge ($0) orders and units in the period. */
  foc_orders_12m: number;
  foc_units_12m: number;
  /** The same length of time just before the period. */
  revenue_prev_12m: number;
  /** The last 90 days and the 90 before: the comparison behind `trend`, declining and expanding. */
  revenue_90d: number;
  revenue_prev_90d: number;
  last_order_date: string | null;
  last_foc_date: string | null;
  trend: FacilityTrend;
  /** Shrinking / Expanding: the last 90 days against the 90 before (AccountSignals). */
  declining?: boolean;
  expanding?: boolean;
  /** Rep mode only: covered through an open territory rather than the rep's own; its numbers are
   *  the orders this rep placed there. */
  open?: boolean;
}
/** The buyer company a facility belongs to, with how many locations it has. */
export interface ParentAccount {
  id: number;
  name: string;
  facility_count: number;
}
export interface RecentProduct {
  name: string;
  quantity: number;
  last_ordered: string;
}
/** The postal-code reference row for one ZIP — city/county/state plus whether the ZCTA polygon
 *  exists to draw it on the map. */
export interface ZipRef {
  zip: string;
  city: string;
  county_name: string;
  state_code: string;
  has_polygon: boolean;
}
/** One coverage-history entry: who covered this facility, and for how long. `valid_to` null
 *  means the row is the facility's current coverage. */
export interface CoverageHistoryRow {
  user_id: number | null;
  source: EffectiveRow['source'];
  territory_id: number | null;
  region_id: number | null;
  valid_from: string;
  valid_to: string | null;
  audit_id: number;
  audit_summary: string;
}
/** `/facility/<id>`: a row plus the per-facility detail the list endpoint does not carry. */
export interface FacilityDetail extends FacilityRow {
  recent_products: RecentProduct[];
  pinned: boolean;
  pinned_via: OverrideGrain | null;
  parent_account: ParentAccount | null;
  zip_ref: ZipRef | null;
  /** Last 20 coverage-history entries for this facility, newest first. */
  history: CoverageHistoryRow[];
}
export type FacilityAction =
  | 'move'
  | 'add'
  | 'remove'
  | 'reset'
  | 'house'
  | 'unpin';
export interface FacilityActionBody {
  user_id?: number;
  note?: string;
  apply_to?: ApplyTo;
}
export type BulkFacilityAction = 'move' | 'add' | 'reset' | 'house' | 'unpin';
export interface AuditRow {
  id: number;
  actor_user_id: number;
  /** "email (id)" from the audit list; absent on write results */
  actor_label?: string;
  action: string;
  summary: string;
  note: string | null;
  undone_by_audit_id: number | null;
  created_date: string;
  facility_count: number;
}
export interface WriteResult {
  audit: AuditRow;
  affected_facilities: number;
  metrics: Record<number, TerritoryMetrics>;
  totals: Tree['totals'];
  region?: Region;
  territory?: Territory;
}
export interface RepCompany {
  id: number;
  name: string;
}
/** A seller company whose territory map this admin may open, with its own rep pool. */
export interface SellerCompany {
  id: number;
  name: string;
  rep_companies: RepCompany[];
}
export type PullMode = 'full' | 'changes';
/** What a pull from the rep map would write, from its dry run. */
export interface PullPreview {
  territories: {
    added: string[];
    removed: string[];
    changed: string[];
    total_in_file: number;
  };
  zips: {
    added: number;
    moved: number;
    removed: number;
    total_in_file: number;
  };
  facilities_affected: number;
  overrides: number;
  pins: number;
  pins_removed: number;
  scenarios_to_cancel: string[];
}
/** A pull from the rep map (populate), as the background task reports it. */
export interface PopulateStatus {
  state: 'running' | 'previewed' | 'done' | 'failed';
  stage: string;
  mode?: PullMode;
  dry_run?: boolean;
  preview?: PullPreview;
  /** changes mode: what the map changed since the last publish/populate. */
  changes?: string[];
  change_count?: number;
  conflicts?: string[];
  conflict_count?: number;
  already?: number;
  base?: TerritoryBackup;
  cancelled_scenarios?: string[];
  counts?: Record<string, number>;
  warnings?: string[];
  dropped?: string[];
  not_converted?: Record<string, number>;
  reps_outside_pool?: string[];
  error?: string;
}
export interface PopulateInfo {
  /** Administrator (not a seller) with Territory Manager. */
  allowed: boolean;
  has_data: boolean;
  /** Full pull: an admin, before the cutover. */
  available: boolean;
  /** Changes pull: also a publish/populate to compare against. */
  changes_available: boolean;
  /** Reps read territories directly; the rep map tab is hidden. */
  cutover: boolean;
  status: PopulateStatus | null;
}
/** One dump of the live territory set + the whole rep map in S3. */
export interface TerritoryBackup {
  id: number;
  actor_user_id: number | null;
  reason: string;
  counts: Record<string, number>;
  audit_id: number | null;
  note: string | null;
  created_date: string | null;
}
/** What restoring a backup would change. */
export interface BackupPreview {
  backup: TerritoryBackup;
  /** Signed S3 link, valid for 5 minutes. */
  download_url: string;
  territories: {
    errors: string[];
    warnings: string[];
    territories: {
      added: string[];
      removed: string[];
      changed: string[];
      total_in_file: number;
    };
    zips: {
      added: number;
      moved: number;
      removed: number;
      total_in_file: number;
    };
    facilities_affected: number;
  };
  scenarios_to_cancel: string[];
  /** null when no live territory owns rep map rows. */
  map: {
    totals: { added: number; removed: number; unchanged: number };
    groups: RepMapGroup[];
  } | null;
}
export type RestoreTarget = 'territories' | 'map';
/** One sales_representative_map row in a publish preview. */
export interface RepMapRow {
  change: 'added' | 'removed';
  /** Removed, and not left there by the last publish/populate: an edit made to the map since. */
  dba: boolean;
  user_id: string;
  facility_id: string;
  company_id: string;
  state: string;
  postal_code: string;
  email: string;
  region_name: string;
  territory_name: string;
  is_open_territory: string;
  is_exclusion: string;
}
export interface RepMapGroup {
  territory_name: string | null;
  email: string | null;
  added: number;
  removed: number;
  dba_removed: number;
  /** Up to 50 rows of this group. */
  rows: RepMapRow[];
}
export interface RepMapPreview {
  /** false: after the cutover, or no live territory to publish. */
  available: boolean;
  cutover?: boolean;
  /** The last publish or populate; null before the first one. */
  base: TerritoryBackup | null;
  territory_changes: AuditRow[];
  totals: {
    added: number;
    removed: number;
    dba_removed: number | null;
    unchanged: number;
  };
  groups: RepMapGroup[];
}
export interface TerritoryContext {
  company_id: number;
  company_name: string;
  rep_companies: RepCompany[];
  /** Every seller with a territory set, the caller's own company included. One per division. */
  sellers: SellerCompany[];
  /** An administrator: the seller picker shows even when there is one seller. */
  can_switch_seller?: boolean;
  /** True for a Field Representative: every edit is off. */
  read_only: boolean;
  /** The account-signal thresholds (Stopped ordering, Shrinking, Expanding, trend band). */
  signals?: AccountSignals;
  /** NPI leads preview is on for this namespace and the caller is a Territory Manager. */
  leads?: boolean;
}

/** How close a lead sits to a facility we have: same building, same ZIP, or neither. */
export type LeadTier = 'building' | 'zip' | 'none';
/** [prospect id, ZIP, tier, territory id, lat, lng] — compact, the map loads every open lead
 *  at once; lat/lng are null until the lead is geocoded. */
export type LeadPin = [
  number,
  string,
  LeadTier,
  number | null,
  number | null,
  number | null,
];
export interface LeadDetail {
  id: number;
  name: string;
  specialty: string | null;
  npi: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string;
  tier: LeadTier;
  next_to: { id: number; name: string } | null;
  source: string;
  lat: number | null;
  lng: number | null;
  territory: string | null;
  reps: string[];
}

export type ScenarioStatus =
  | 'draft'
  | 'pending'
  | 'scheduled'
  | 'applied'
  | 'rejected'
  /** A full pull from the rep map replaced live while it was open. */
  | 'cancelled';
export type ScenarioAction =
  | 'submit'
  | 'reopen'
  | 'reject'
  | 'approve'
  | 'schedule'
  | 'unschedule';

/** A draft copy of one company's whole territory set, editable through the normal API. */
export interface Scenario {
  id: number;
  company_id: number;
  name: string;
  status: ScenarioStatus;
  note: string | null;
  submitted_by: number | null;
  approved_by: number | null;
  applied_audit_id: number | null;
  base_audit_id: number | null;
  created_by: number | null;
  created_date: string;
  /** Set while `status` is `scheduled`: the UTC date the nightly job applies it on. */
  apply_on: string | null;
  /** Live territories have changed since the scenario forked — apply may not do what it shows.
   *  Computed per request, so it is only present on the list and diff responses. */
  stale?: boolean;
  created_by_name?: string;
}
export interface ScenarioTerritorySide {
  region: string;
  reps: string[];
  is_open: boolean;
  zips: number;
  facilities: number;
  t12m_revenue: number;
}
export interface ScenarioDiffRow {
  name: string;
  status: 'added' | 'removed' | 'changed';
  before: ScenarioTerritorySide | null;
  after: ScenarioTerritorySide | null;
}
export interface ScenarioDiffSummary {
  territories: {
    added: string[];
    removed: string[];
    /** Names, not a count — `ti.dry_run` returns the changed territory names. */
    changed: string[];
    total_in_file: number;
  };
  zips: {
    added: number;
    moved: number;
    removed: number;
    total_in_file: number;
  };
  facilities_affected: number;
  overrides: { added: number; removed: number };
  region_reps_changed: number;
}
/** One ZIP whose owner differs between live and the scenario. `live_*` null = the scenario adds
 *  the ZIP; `scenario_*` null = it removes it; both set = moved between territories. */
export interface ScenarioChangedZip {
  postal_code: string;
  live_territory_id: number | null;
  live_territory: string | null;
  scenario_territory_id: number | null;
  scenario_territory: string | null;
}
export interface ScenarioDiff {
  scenario: Scenario;
  stale: boolean;
  errors: string[];
  warnings: string[];
  summary: ScenarioDiffSummary;
  territories: ScenarioDiffRow[];
  changed_zips: ScenarioChangedZip[];
}
/** One rep's whole workload, for the Reps view and the hand-off preview. */
export interface RepLoad {
  id: number;
  name: string;
  email: string;
  territories: { id: number; name: string }[];
  regions: { id: number; name: string }[];
  overrides: number;
  facilities: number;
  t12m_revenue: number;
  /** How many territories list this rep as primary. */
  primary_territories: number;
}
/** + Rep: `rep` always; the write-result fields only when the rep went straight onto a
 *  territory (that part is an audited, undoable write). */
export type NewRepResult = {
  rep: { id: number; name: string; email: string };
} & Partial<WriteResult>;
/** One Territory Planner turn (POST /ai-chat). `map_actions` are applied by the territories page. */
export interface TerritoryAiReply {
  answer: string;
  charts: AiChatChartSpec[];
  map_actions: Record<string, unknown>[];
}

/** The Ask AI panel's opening card (GET /ai-summary): generated wording over computed facts. */
export interface TerritoryAiSummary {
  summary: string;
  flags: { severity: 'high' | 'med' | 'low'; text: string }[];
  advice: string[];
  suggestions: string[];
}

/** One facility as the brief (and the Ask AI tools) report it. */
export interface BriefFacility {
  id: number;
  name: string;
  city: string;
  state: string;
  zip: string | null;
  reps: string[];
  orders_12m: number;
  revenue_12m: number;
  units_12m: number;
  foc_orders_12m: number;
  foc_units_12m: number;
  revenue_last_90d: number;
  revenue_prior_90d: number;
  quarter_change: string | null;
  last_order_date: string | null;
  last_foc_date: string | null;
  /** Call lists only (lapsed, declining): the facility's own contact fields. */
  contact?: {
    name: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  /** The territory it sits in, by name. */
  territory?: string | null;
  /** Exceptions only: why it doesn't follow the ZIP rule, in words. */
  kinds?: string[];
}
export interface MonthRevenue {
  month: string; // YYYY-MM
  revenue: number;
  /** The period cuts into this month, or it isn't over yet. */
  partial: boolean;
}
export interface BriefList {
  count: number;
  facilities: BriefFacility[];
}
export interface TerritoryBrief {
  territory: {
    id: number;
    name: string;
    code: string | null;
    is_open: boolean;
    region: string | null;
  };
  reps: string[];
  revenue: number;
  revenue_prev_period: number;
  revenue_change: string | null;
  units: number;
  foc_orders: number;
  foc_units: number;
  /** Facilities with any free-of-charge order in the period. */
  foc_accounts: number;
  balance: {
    value: number | null;
    vs_mean: string | null;
    status: 'over' | 'under' | 'ok' | null;
    rank: string | null;
    mean: number;
  } | null; // null on a rep's own brief (rep mode)
  balance_tolerance_pct: number;
  monthly_revenue: MonthRevenue[];
  top_accounts: BriefList;
  lapsed: BriefList;
  foc_only: BriefList;
  declining: BriefList;
  exceptions: BriefList;
  /** AI-written opening; null when the model was unavailable, absent when not asked for. */
  summary?: { summary: string; next_steps: string[] } | null;
  recent_changes: {
    count: number;
    changes: {
      when: string;
      who: string;
      what: string;
      facilities_affected: number;
      undone: boolean;
    }[];
  };
}

/** One territory's line in a region overview. */
export interface RegionTerritoryRow {
  id: number;
  territory: string;
  is_open: boolean;
  reps: string[];
  facilities: number;
  active_accounts: number;
  facilities_per_rep: number | null;
  vs_mean: string | null;
  status: 'over' | 'under' | 'ok' | null;
  revenue: number;
  revenue_change: string | null;
  revenue_share: string | null;
  units: number;
  foc_units: number;
  foc_only: number;
  lapsed: number;
  lapsed_revenue: number;
  declining: number;
  exceptions: number;
}
export interface RegionBrief {
  region: {
    id: number | null;
    name: string;
    code: string | null;
    parent: string | null;
  };
  region_reps: string[];
  totals: {
    territories: number;
    facilities: number;
    active_accounts: number;
    reps_with_account: number;
    revenue: number;
    revenue_prev_period: number;
    revenue_change: string | null;
    overloaded: number;
    underloaded: number;
    territories_needing_a_rep: number;
    lapsed: number;
    lapsed_revenue: number;
    declining: number;
    exceptions: number;
  };
  foc: {
    units: number;
    foc_orders: number;
    foc_units: number;
    foc_accounts: number;
  };
  balance_mean: number;
  balance_tolerance_pct: number;
  monthly_revenue: MonthRevenue[];
  sub_regions: {
    name: string;
    territories: number;
    facilities: number;
    revenue: number;
    revenue_change: string | null;
    revenue_share: string | null;
    overloaded: number;
    lapsed: number;
    lapsed_revenue: number;
  }[];
  territories: RegionTerritoryRow[];
  lapsed: BriefList;
  foc_only: BriefList;
  declining: BriefList;
  summary?: { summary: string; next_steps: string[] } | null;
}

/** The rep overview: load vs the other reps, one line per territory they cover (directly or
 *  through a region), and the accounts to act on among the ones they cover. */
export interface RepBrief {
  rep: { id: number; name: string; email: string | null };
  direct_territories: string[];
  regions: string[];
  totals: {
    territories: number;
    facilities: number;
    active_accounts: number;
    revenue: number;
    revenue_prev_period: number;
    revenue_change: string | null;
    facilities_vs_rep_average: string | null;
    rep_average_facilities: number;
    rank_by_facilities: string | null;
    overloaded_territories: number | null;
    accounts_outside_territories: number;
    overrides: number;
    lapsed: number;
    lapsed_revenue: number;
    declining: number;
  };
  foc: RegionBrief['foc'];
  monthly_revenue: MonthRevenue[];
  balance_mean: number | null; // null on a rep's own overview (rep mode)
  balance_tolerance_pct: number;
  territories: RegionTerritoryRow[];
  top_accounts: BriefList;
  lapsed: BriefList;
  declining: BriefList;
  foc_only: BriefList;
  recent_changes: TerritoryBrief['recent_changes'];
  summary?: { summary: string; next_steps: string[] } | null;
}

/** The facility brief's lead line: the one signal it earns, priority order. */
export type FacilityBriefSignal =
  | 'expanding'
  | 'shrinking'
  | 'stopped_ordering'
  | 'foc_only'
  | 'no_orders';
export interface FacilityBriefMonth {
  month: string; // YYYY-MM
  orders: number;
  units: number;
  revenue: number;
  foc_units: number;
}
/** One coverage period as the brief words it (the rep endpoint has no `change`). */
export interface FacilityBriefHistoryRow {
  rep: string;
  source: string;
  from: string;
  to: string | null;
  change: string | null;
}
/** One facility's brief: coverage, the last 12 months by month, its signal and top products. */
export interface FacilityBrief {
  facility: {
    id: number;
    name: string;
    city: string;
    state: string;
    zip: string | null;
    territory: { id: number; name: string; is_open: boolean } | null;
    reps: string[];
  };
  signal: FacilityBriefSignal | null;
  /** Set with `no_orders`: how many months without an order. */
  no_orders_months: number | null;
  trend: FacilityTrend;
  last_order_date: string | null;
  last_foc_date: string | null;
  totals_12m: Omit<FacilityBriefMonth, 'month'>;
  /** 12 months, oldest first. */
  monthly: FacilityBriefMonth[];
  top_products: RecentProduct[];
  history: FacilityBriefHistoryRow[];
  summary: string | null;
  next_steps: string[];
}

export interface HandoffResult extends WriteResult {
  moved: { territories: number; regions: number; overrides: number };
}

export interface ScenarioStatusResult {
  scenario: Scenario;
}
/** One coverage period from v_facility_coverage_story: who covered a facility, why, when, and
 *  which change started and ended it. `valid_to` null = still covering. */
export interface CoverageStoryRow {
  id: number;
  facility_id: number;
  facility_name: string | null;
  user_id: number | null;
  rep_label: string;
  source: string;
  territory_id: number | null;
  territory_name: string | null;
  region_id: number | null;
  region_name: string | null;
  valid_from: string;
  valid_to: string | null;
  started_by_audit_id: number | null;
  started_by_action: string | null;
  started_by_summary: string | null;
  started_by_note: string | null;
  started_by_label: string | null;
  ended_by_audit_id: number | null;
  ended_by_action: string | null;
  ended_by_summary: string | null;
  ended_by_note: string | null;
  ended_by_label: string | null;
}
/** Coverage tab filters; every field optional. `as_of` wins over since/until. */
export interface CoverageQuery {
  facility_id?: number;
  user_id?: number;
  territory_id?: number;
  q?: string;
  as_of?: string;
  since?: string;
  until?: string;
}
/** `approve` applies the draft to live territories, so it also returns the live write result. */
export interface ScenarioApplyResult extends WriteResult {
  scenario: Scenario;
}

/** Rep mode: which accounts the rep reads — their own, open-territory ones, or both. */
export type RepCoverage = 'mine' | 'open' | 'all';
/** Ask AI's map selection: whole states by code, plus the ZIPs outside them (5-digit strings). */
export interface AiSelection {
  states: string[];
  zips: string[];
}
/** Ask AI's map context: what the map shows when the question is sent. Only the parts that are
 *  set are sent; the backend defaults the rest (period: the last 12 months). */
export interface AiMapContext {
  territory_id?: number;
  region_id?: number;
  rep_user_id?: number;
  facility_id?: number;
  selection?: AiSelection;
  pin_filter?: string;
  facility_status?: string;
  pin_mode?: string;
  color_by?: string;
  period?: { from: string; to: string };
  /** The area card's Show row, when narrowed. */
  area_statuses?: string[];
}

@Injectable({ providedIn: 'root' })
export class TerritoryService {
  /** Rep mode (`/territories/my`): the same page over the rep's own coverage, read-only. Every
   *  call goes to `/territories/me`, which answers in the seller shapes from the rep's rows only;
   *  the calls with no rep twin (other reps, audit, scenarios, AI) answer empty without a request.
   *  Set by the page from its route before the first call. */
  repView = false;
  private get base(): string {
    const root = `${environment.api_url_v1}/services/user/territories`;
    return this.repView ? `${root}/me` : root;
  }
  /** Active scenario scope. Every scoped call carries it, so the whole territory API reads and
   *  writes the draft copy instead of live territories. Owned by TerritoryStateService. */
  scenarioId: number | null = null;
  /** Revenue period (YYYY-MM-DD, inclusive) every call carries, or null for the default last
   *  12 months. Owned by TerritoryStateService. */
  period: { from: string; to: string } | null = null;
  /** Rep mode: sent as `coverage` on every rep read (the briefs ignore it). Owned by
   *  TerritoryStateService. */
  repCoverage: RepCoverage = 'mine';
  /** How many territory requests are in flight; the page shows its activity bar while > 0. */
  readonly pending$ = new BehaviorSubject<number>(0);
  constructor(private http: HttpClient) {}
  /** Count a request for the whole time it is on the wire. `defer` so the count follows each
   *  subscription rather than the moment the observable was built. */
  private track<T>(request$: Observable<T>): Observable<T> {
    return defer(() => {
      this.pending$.next(this.pending$.value + 1);
      return request$.pipe(
        finalize(() => this.pending$.next(this.pending$.value - 1)),
      );
    });
  }
  /** Request body scope for a write. */
  private scope(companyId: number): {
    company_id: number;
    scenario_id?: number;
  } {
    return {
      company_id: companyId,
      ...(this.scenarioId ? { scenario_id: this.scenarioId } : {}),
    };
  }
  /** Query params for a scoped call. */
  private p(companyId: number, extra: Record<string, string> = {}): HttpParams {
    return this.live(companyId, {
      ...extra,
      ...(this.scenarioId ? { scenario_id: String(this.scenarioId) } : {}),
      ...(this.repView ? { coverage: this.repCoverage } : {}),
    });
  }
  /** Query params that never carry the scenario: the scenario endpoints themselves, and
   *  `/context`, always address the real company. */
  private live(
    companyId: number,
    extra: Record<string, string> = {},
  ): HttpParams {
    return new HttpParams({
      fromObject: { company_id: String(companyId), ...this.period, ...extra },
    });
  }
  /** `rawErrors`: fail with the HttpErrorResponse, for a caller that branches on its status. */
  context(rawErrors = false): Observable<{ data: TerritoryContext }> {
    return this.track(
      this.http.get<{ data: TerritoryContext }>(`${this.base}/context`, {
        context: new HttpContext().set(RAW_HTTP_ERROR, rawErrors),
      }),
    );
  }
  tree(c: number, includeRetired = false): Observable<{ data: Tree }> {
    return this.track(
      this.http.get<{ data: Tree }>(`${this.base}/tree`, {
        params: this.p(c, includeRetired ? { include_retired: '1' } : {}),
      }),
    );
  }
  /** `/zips/ref`: the postal-code reference for up to 500 ZIPs per call. */
  zipsRef(
    c: number,
    zips: string[],
  ): Observable<{ data: Record<string, ZipRef> }> {
    return this.track(
      this.http.get<{ data: Record<string, ZipRef> }>(`${this.base}/zips/ref`, {
        params: this.p(c, { zips: zips.join(',') }),
      }),
    );
  }
  zips(c: number): Observable<{ data: Record<string, number> }> {
    return this.track(
      this.http.get<{ data: Record<string, number> }>(`${this.base}/zips`, {
        params: this.p(c),
      }),
    );
  }
  facilities(
    c: number,
    q: {
      zips?: string[];
      ids?: number[];
      filter?: string;
      territory_id?: number;
    },
  ): Observable<{ data: FacilityRow[] }> {
    const extra: Record<string, string> = {};
    if (q.zips) extra['zips'] = q.zips.join(',');
    if (q.ids) extra['ids'] = q.ids.join(',');
    if (q.filter) extra['filter'] = q.filter;
    if (q.territory_id != null) extra['territory_id'] = String(q.territory_id);
    return this.track(
      this.http.get<{ data: FacilityRow[] }>(`${this.base}/facilities`, {
        params: this.p(c, extra),
      }),
    );
  }
  facility(c: number, id: number): Observable<{ data: FacilityDetail }> {
    return this.track(
      this.http.get<{ data: FacilityDetail }>(`${this.base}/facility/${id}`, {
        params: this.p(c),
      }),
    );
  }
  prospects(c: number) {
    return this.http.get<{ data: { leads: LeadPin[] } }>(
      `${this.base}/prospects`,
      {
        params: this.p(c),
      },
    );
  }
  prospect(c: number, id: number) {
    return this.http.get<{ data: LeadDetail }>(`${this.base}/prospects/${id}`, {
      params: this.p(c),
    });
  }
  territoryBrief(c: number, id: number, summary = true) {
    return this.track(
      this.http.get<{ data: TerritoryBrief }>(`${this.base}/${id}/brief`, {
        params: this.p(c, summary ? { summary: '1' } : {}),
      }),
    );
  }
  /** `open` is the Open group. */
  repBrief(c: number, userId: number) {
    return this.track(
      // a rep only ever gets their own overview
      this.http.get<{ data: RepBrief }>(
        this.repView
          ? `${this.base}/brief`
          : `${this.base}/reps/${userId}/brief`,
        {
          params: this.p(c, { summary: '1' }),
        },
      ),
    );
  }
  regionBrief(c: number, id: number | 'open') {
    if (this.repView) return EMPTY;
    return this.track(
      this.http.get<{ data: RegionBrief }>(`${this.base}/regions/${id}/brief`, {
        params: this.p(c, { summary: '1' }),
      }),
    );
  }
  /** The model summary is on by default here; a rep gets a 403 for an open facility. */
  facilityBrief(c: number, id: number) {
    return this.track(
      this.http.get<{ data: FacilityBrief }>(
        `${this.base}/facility/${id}/brief`,
        { params: this.p(c) },
      ),
    );
  }
  selection(c: number, zips: string[]) {
    return this.track(
      this.http.post<{ data: any }>(
        `${this.base}/selection`,
        { zips },
        { params: this.p(c) },
      ),
    );
  }
  repPool(c: number, repCompanyId?: number) {
    const extra: Record<string, string> = {};
    if (repCompanyId) extra['rep_company_id'] = String(repCompanyId);
    return this.track(
      this.http.get<{ data: Rep[] }>(`${this.base}/rep-pool`, {
        params: this.p(c, extra),
      }),
    );
  }
  audit(c: number, page = 1, size = 50) {
    if (this.repView)
      return of({ data: { items: [] as AuditRow[], total: 0 } });
    return this.track(
      this.http.get<{ data: { items: AuditRow[]; total: number } }>(
        `${this.base}/audit`,
        { params: this.p(c, { page: String(page), size: String(size) }) },
      ),
    );
  }
  auditRows(c: number, id: number) {
    return this.track(
      this.http.get<{ data: { facility_ids: number[]; zips: string[] } }>(
        `${this.base}/audit/${id}/rows`,
        { params: this.p(c) },
      ),
    );
  }
  exportUrl(c: number) {
    const scenario = this.scenarioId ? `&scenario_id=${this.scenarioId}` : '';
    return `${this.base}/export?company_id=${c}${scenario}`;
  }

  createRegion(
    c: number,
    body: { name: string; parent_id?: number | null; code?: string },
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/regions`, {
        ...body,
        ...this.scope(c),
      }),
    );
  }
  updateRegion(
    c: number,
    id: number,
    body: { name?: string; parent_id?: number | null; code?: string },
  ) {
    return this.track(
      this.http.patch<{ data: WriteResult }>(`${this.base}/regions/${id}`, {
        ...body,
        ...this.scope(c),
      }),
    );
  }
  /** DELETE now retires the region (its live territories/sub-regions must already be gone). */
  retireRegion(c: number, id: number) {
    return this.track(
      this.http.delete<{ data: WriteResult }>(`${this.base}/regions/${id}`, {
        params: this.p(c),
      }),
    );
  }
  reactivateRegion(c: number, id: number) {
    return this.track(
      this.http.post<{ data: WriteResult }>(
        `${this.base}/regions/${id}/reactivate`,
        { ...this.scope(c) },
      ),
    );
  }
  setRegionReps(c: number, id: number, user_ids: number[]) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/regions/${id}/reps`, {
        user_ids,
        ...this.scope(c),
      }),
    );
  }
  createTerritory(
    c: number,
    body: {
      name: string;
      region_id: number;
      zips: string[];
      user_ids: number[];
      is_open: boolean;
      note?: string;
      code?: string;
    },
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}`, {
        ...body,
        ...this.scope(c),
      }),
    );
  }
  updateTerritory(
    c: number,
    id: number,
    body: {
      name?: string;
      region_id?: number;
      is_open?: boolean;
      code?: string;
    },
  ) {
    return this.track(
      this.http.patch<{ data: WriteResult }>(`${this.base}/${id}`, {
        ...body,
        ...this.scope(c),
      }),
    );
  }
  /** DELETE now retires the territory: its ZIPs become unassigned and the name is reusable. */
  deleteTerritory(c: number, id: number) {
    return this.track(
      this.http.delete<{ data: WriteResult }>(`${this.base}/${id}`, {
        params: this.p(c),
      }),
    );
  }
  reactivateTerritory(c: number, id: number) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/${id}/reactivate`, {
        ...this.scope(c),
      }),
    );
  }
  setTerritoryReps(
    c: number,
    id: number,
    user_ids: number[],
    primary_user_id?: number,
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/${id}/reps`, {
        user_ids,
        ...(primary_user_id != null ? { primary_user_id } : {}),
        ...this.scope(c),
      }),
    );
  }
  moveZips(
    c: number,
    zips: string[],
    to_territory_id: number | null,
    note?: string,
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/zips/move`, {
        zips,
        to_territory_id,
        note,
        ...this.scope(c),
      }),
    );
  }
  facilityAction(
    c: number,
    id: number,
    action: FacilityAction,
    body: FacilityActionBody = {},
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(
        `${this.base}/facility/${id}/${action}`,
        { ...body, ...this.scope(c) },
      ),
    );
  }
  /** Pin with no reps: the facility leaves the ZIP rules and has no field rep (CS only). */
  houseFacility(
    c: number,
    id: number,
    body: Pick<FacilityActionBody, 'note' | 'apply_to'> = {},
  ) {
    return this.facilityAction(c, id, 'house', body);
  }
  /** Drop the pin only; overrides stay. `reset` drops both. */
  unpinFacility(
    c: number,
    id: number,
    body: Pick<FacilityActionBody, 'note' | 'apply_to'> = {},
  ) {
    return this.facilityAction(c, id, 'unpin', body);
  }
  facilitiesBulk(
    c: number,
    facility_ids: number[],
    action: BulkFacilityAction,
    user_id?: number,
    note?: string,
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/facilities/bulk`, {
        facility_ids,
        action,
        user_id,
        note,
        ...this.scope(c),
      }),
    );
  }
  undo(c: number, auditId: number) {
    return this.track(
      this.http.post<{ data: WriteResult }>(
        `${this.base}/audit/${auditId}/undo`,
        { ...this.scope(c) },
      ),
    );
  }
  rebuild(c: number) {
    return this.track(
      this.http.post<{ data: any }>(`${this.base}/rebuild`, {
        ...this.scope(c),
      }),
    );
  }
  importDryRun(c: number, file: File, mode: 'replace' | 'merge') {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    fd.append('company_id', String(c));
    if (this.scenarioId) fd.append('scenario_id', String(this.scenarioId));
    return this.track(
      this.http.post<{ data: { token: string; diff: any } }>(
        `${this.base}/import/dry-run`,
        fd,
        { params: this.p(c) },
      ),
    );
  }
  /** Populate from the legacy rep map: whether it is offered for this seller, and the last run. */
  populateStatus(c: number) {
    return this.http.get<{ data: PopulateInfo }>(`${this.base}/populate`, {
      params: { company_id: String(c) },
    });
  }
  /** Live only (never a scenario): it starts an empty seller. Not tracked — it runs in the
   *  background and the tab shows its own progress. */
  /** Admins: what Publish would change in the rep map. Slow (runs the rewrite and rolls it back). */
  repMapPreview(c: number) {
    return this.http.get<{ data: RepMapPreview }>(
      `${this.base}/rep-map/publish`,
      {
        params: { company_id: String(c) },
      },
    );
  }

  /** Territory Managers: a named dump of the live territories + the whole rep map. */
  takeBackup(c: number, note: string) {
    return this.http.post<{ data: TerritoryBackup }>(`${this.base}/backups`, {
      company_id: c,
      note,
    });
  }

  /** The backups, newest first — anyone who can see territories; restoring stays with admins. */
  repMapBackups(c: number, page: number, size: number) {
    return this.http.get<{
      data: {
        items: (TerritoryBackup & { actor_label: string })[];
        total: number;
      };
    }>(`${this.base}/rep-map/backups`, {
      params: { company_id: String(c), page: String(page), size: String(size) },
    });
  }

  /** Admins: what restoring a backup would change. Slow (reads the dump from S3). */
  repMapBackupPreview(c: number, backupId: number) {
    return this.http.get<{ data: BackupPreview }>(
      `${this.base}/rep-map/backups/${backupId}`,
      { params: { company_id: String(c) } },
    );
  }

  /** Admins: restore this seller's territories, or the rep map rows territories own. */
  restoreBackup(c: number, backupId: number, target: RestoreTarget) {
    return this.http.post<{ data: { backup: TerritoryBackup } }>(
      `${this.base}/rep-map/backups/${backupId}/restore`,
      { company_id: c, target },
    );
  }

  /** Admins: dump, then rewrite the rep map rows the territories own. */
  publishRepMap(c: number) {
    return this.http.post<{
      data: { backup: TerritoryBackup; deleted: number; inserted: number };
    }>(`${this.base}/rep-map/publish`, { company_id: c });
  }

  /** Starts a pull from the rep map; `dryRun` only previews it (the status carries the preview).
   *  `note` goes on the write's dump and audit entry. */
  populateFromRepMap(c: number, mode: PullMode, dryRun: boolean, note?: string) {
    return this.http.post<{ data: { status: PopulateStatus } }>(
      `${this.base}/populate`,
      { company_id: c, mode, dry_run: dryRun, note },
    );
  }
  importApply(
    c: number,
    token: string,
    mode: 'replace' | 'merge',
    note?: string,
  ) {
    return this.track(
      this.http.post<{ data: WriteResult }>(`${this.base}/import/apply`, {
        token,
        mode,
        note,
        ...this.scope(c),
      }),
    );
  }

  /** Not tracked: the chat panel shows its own progress, and a long answer should not hold the
   *  page's activity bar. */
  aiChat(
    c: number,
    scenarioId: number | null,
    message: string,
    history: readonly AiChatTurn[],
    context: AiMapContext,
  ) {
    return this.http.post<{ data: TerritoryAiReply }>(
      `${this.base}/ai-chat`,
      {
        company_id: c,
        scenario_id: scenarioId,
        message,
        history,
        context,
      },
      { params: this.live(c) },
    );
  }
  aiSummary(c: number, scenarioId: number | null) {
    if (this.repView) return EMPTY;
    const extra: Record<string, string> = scenarioId
      ? { scenario_id: String(scenarioId) }
      : {};
    return this.http.get<{ data: TerritoryAiSummary }>(
      `${this.base}/ai-summary`,
      {
        params: this.live(c, extra),
      },
    );
  }
  scenarios(c: number) {
    if (this.repView) return of({ data: [] as Scenario[] });
    return this.track(
      this.http.get<{ data: Scenario[] }>(`${this.base}/scenarios`, {
        params: this.live(c),
      }),
    );
  }
  createScenario(c: number, name: string) {
    return this.track(
      this.http.post<{ data: ScenarioStatusResult }>(`${this.base}/scenarios`, {
        name,
        company_id: c,
      }),
    );
  }
  /** Live coverage history only — scenario copies write none, so no scenario_id is sent. */
  coverage(c: number, query: CoverageQuery, page: number, size: number) {
    const extra: Record<string, string> = {
      page: String(page),
      size: String(size),
    };
    for (const [key, value] of Object.entries(query))
      if (value != null && value !== '') extra[key] = String(value);
    return this.track(
      this.http.get<{ data: { items: CoverageStoryRow[]; total: number } }>(
        `${this.base}/coverage`,
        { params: this.live(c, extra) },
      ),
    );
  }
  /** The division's shared "Pending changes" draft: reused if open, forked on first use. */
  sharedScenario(c: number) {
    if (this.repView) return EMPTY;
    return this.track(
      this.http.post<{ data: ScenarioStatusResult }>(
        `${this.base}/scenarios/shared`,
        { company_id: c },
      ),
    );
  }
  scenarioDiff(c: number, id: number) {
    return this.track(
      this.http.get<{ data: ScenarioDiff }>(
        `${this.base}/scenarios/${id}/diff`,
        { params: this.live(c) },
      ),
    );
  }
  /** `force` overrides the backend's stale-scenario refusal: only send it once the approver has
   *  been shown that live territories moved since the fork. */
  scenarioAction(
    c: number,
    id: number,
    action: 'approve',
    note?: string,
    force?: boolean,
  ): Observable<{ data: ScenarioApplyResult }>;
  scenarioAction(
    c: number,
    id: number,
    action: Exclude<ScenarioAction, 'approve'>,
    note?: string,
  ): Observable<{ data: ScenarioStatusResult }>;
  scenarioAction(
    c: number,
    id: number,
    action: ScenarioAction,
    note?: string,
    force?: boolean,
  ) {
    return this.track(
      this.http.post<{ data: ScenarioApplyResult | ScenarioStatusResult }>(
        `${this.base}/scenarios/${id}/${action}`,
        { note, force, company_id: c },
      ),
    );
  }
  deleteScenario(c: number, id: number) {
    return this.track(
      this.http.delete<{ data: { deleted: number } }>(
        `${this.base}/scenarios/${id}`,
        { params: this.live(c) },
      ),
    );
  }
  /** `pending` → `scheduled`. `apply_on` is a `YYYY-MM-DD` date; the backend 400s unless it is
   *  tomorrow-or-later UTC. */
  scheduleScenario(c: number, id: number, apply_on: string) {
    return this.track(
      this.http.post<{ data: ScenarioStatusResult }>(
        `${this.base}/scenarios/${id}/schedule`,
        { apply_on, company_id: c },
      ),
    );
  }
  /** `scheduled` → `pending`. */
  unscheduleScenario(c: number, id: number) {
    return this.track(
      this.http.post<{ data: ScenarioStatusResult }>(
        `${this.base}/scenarios/${id}/unschedule`,
        { company_id: c },
      ),
    );
  }

  reps(c: number, repCompanyId?: number) {
    if (this.repView) return of({ data: [] as RepLoad[] });
    const extra: Record<string, string> = {};
    if (repCompanyId) extra['rep_company_id'] = String(repCompanyId);
    return this.track(
      this.http.get<{ data: RepLoad[] }>(`${this.base}/reps`, {
        params: this.p(c, extra),
      }),
    );
  }
  createRep(
    c: number,
    body: {
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      rep_company_id: number;
      territory_id?: number;
      send_invite: boolean;
    },
  ) {
    return this.track(
      this.http.post<{ data: NewRepResult }>(`${this.base}/reps`, {
        ...body,
        ...this.scope(c),
      }),
    );
  }
  repHandoff(
    c: number,
    from_user_id: number,
    to_user_id: number,
    note?: string,
  ) {
    return this.track(
      this.http.post<{ data: HandoffResult }>(`${this.base}/reps/handoff`, {
        from_user_id,
        to_user_id,
        note,
        ...this.scope(c),
      }),
    );
  }
}
